<template>
  <div id="chart">
    <div id="chart-container">
      <svg id="canvas" :height="height" :width="width" ref="svgcanvas">
        <g id="backgrounds">
          <!-- one rect below each column -->
          <rect
            :x="0"
            :y="0"
            :width="width"
            :height="height"
            fill="#fff"
          ></rect>
          <rect
            :x="0"
            y="0"
            rx="6"
            ry="6"
            class="stroke-2 stroke-Scale-50"
            :width="BG_RECTANGLE_WIDTH"
            :height="height"
            fill="#fff"
          ></rect>
          <rect
            :x="width / 2 - BG_RECTANGLE_WIDTH / 2"
            y="0"
            rx="6"
            ry="6"
            class="stroke-2 stroke-Scale-50"
            :width="BG_RECTANGLE_WIDTH"
            :height="height"
            fill="#fff"
          ></rect>
          <rect
            :x="width - BG_RECTANGLE_WIDTH"
            y="0"
            rx="6"
            ry="6"
            class="stroke-2 stroke-Scale-50"
            :width="BG_RECTANGLE_WIDTH"
            :height="height"
            fill="#fff"
          ></rect>
        </g>
        <g
          :transform="`translate(${SANKEY_GLOBAL_PADDING_X},${SANKEY_GLOBAL_PADDING_Y})`"
          ref="svg"
        ></g>
      </svg>
      <!---->
      <!-- <div
        v-if="tooltip.showTooltip"
        id="tooltip"
        :style="{ left: adjustedTooltipX + 'px', top: tooltip.y + 'px' }"
      >
        <div class="tooltip-h mb-1">
          {{ $formater.salaryFormater(tooltip.value) }}
        </div>
       
      </div> -->
      <ClientOnly>
        <template v-if="tooltip.tooltipPosition.value.owner === this.tooltipId">
          <Teleport to="#teleport-tooltipchart">
            <div class="text-2xl text-Primary-800">
              <span class="font-bold">
                {{ $formater.salaryFormater(hoveredData.value) }}
              </span>
            </div>
            <div class="text-sm">
              <div v-if="extraBilateralInfo && hoveredData.type === 'donor'">
                Amount includes Bilateral, unspecified
              </div>
              <template v-else>
                <span>{{ hoveredData.source_title }}</span>
                <span v-if="hoveredData.target_title">
                  &rarr; {{ hoveredData.target_title }}</span
                >
              </template>
            </div>
          </Teleport>
        </template>
      </ClientOnly>
      <!-- modal for  info about categories -->
      <InterfaceModalBase
        :showModal="showCategoriesModal"
        @update:showModal="showCategoriesModal = $event"
        size-tailwind-class="sm:max-w-xl"
        :showButtons="false"
      >
        <template #heading> Category </template>
        <template #title>
          <span class="text-xl"
            >{{ clickedCategory?.toLocaleUpperCase() }}
          </span>
        </template>

        <template #content>
          <div class="flex flex-col justify-start">
            <p class="text-left">
              {{ getCategoryDefinition(clickedCategory) }}
            </p>
          </div>
        </template>
      </InterfaceModalBase>
    </div>
    <div class="sankey-legend mt-2 flex justify-end" v-if="showLegend">
      <div class="justify-end flex flex-col gap-2">
        <div class="flex justify-start gap-4">
          <div class="w-6 h-4 shrink-0 bg-[#D4D5E1]" />
          <div class="text-xs">Countries</div>
        </div>
        <div class="flex justify-start gap-4">
          <div class="w-6 h-4 shrink-0 bg-[#FECDCE]" />
          <div class="text-xs">Regional Entities</div>
        </div>
      </div>
    </div>
    <ClientOnly>
      <InterfaceSvgExportButtons v-if="svgNode" :svg-node="svgNode" filename="sankey-chart" />
    </ClientOnly>
  </div>
</template>
<script>
import * as d3 from "d3";
import * as d3sankey from "d3-sankey";
import { textWrap } from "d3plus-text";
import { categoryColorScale } from "@/assets/baseTypes.js";
import categoriesDefinition from "@/assets/categories.json";

const VERT_PADDING = 10;
const TEXT_SPACING_EXTREMES = 80;
const TEXT_PADDING = VERT_PADDING;
const NODE_WIDTH = 126;
const NODE_PADDING = 10;
const SMALL_NODE_WIDTH = 20;
const CENTER_NODE_WIDTH = NODE_WIDTH - VERT_PADDING;

const SANKEY_GLOBAL_PADDING_X = 50;
const SANKEY_GLOBAL_PADDING_Y = 40;
const BG_RECTANGLE_WIDTH = NODE_WIDTH * 2 + SANKEY_GLOBAL_PADDING_X;
function splitText(text, w, h, maxLines) {
  return textWrap()
    .height(h)
    .width(w)
    .fontSize(12)
    .lineHeight(1.3)
    .maxLines(maxLines)
    .overflow(false)(text);
}

import slugify from "slugify";
import { nodeTypes } from "@/assets/configStrings.js";

//Reference: sankey with d3 v5 https://jarrettmeyer.com/2018/05/31/creating-a-d3-sankey-graph
function getNodeWidth(node) {
  if (node.type === nodeTypes.category) {
    return NODE_WIDTH - VERT_PADDING * 2;
  } else return 20;
}

function getNodePosition(d) {
  if (d.type === nodeTypes.category) {
    return `translate(${d.x0 + VERT_PADDING}, ${d.y0})`;
  }
  if (d.type === nodeTypes.donor) {
    return `translate(${d.x0 + TEXT_SPACING_EXTREMES}, ${d.y0})`;
  }
  if (d.type === nodeTypes.recipient) {
    return `translate(${d.x0 + VERT_PADDING}, ${d.y0})`;
  }
}
export default {
  setup() {
    const tooltip = useTooltip();
    return { tooltip };
  },
  name: "",
  props: {
    chartData: {
      type: Object,
      default: null,
    },
    options: {
      type: Object,
      default: null,
    },
    totalWidth: {
      type: Number,
      default: 1400,
    },
    height: {
      type: Number,
      default: 2600,
    },
    autoSorting: {
      type: Boolean,
      default: true,
    },
    showLegend: {
      type: Boolean,
      default: false,
    },
    extraBilateralInfo: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    graph: { nodes: [], links: [] },
    sankey: null,
    defs: null,
    numberLayers: 2,
    SANKEY_GLOBAL_PADDING_X,
    SANKEY_GLOBAL_PADDING_Y,
    BG_RECTANGLE_WIDTH,
    hoveredData: {},
    tooltipId: "sankey-tooltip",
    showCategoriesModal: false,
    clickedCategory: "",
  }),
  computed: {
    width: function () {
      return this.totalWidth;
    },

    totalNodes: function () {
      this.graph.nodes.filter;
      return d3.rollup(
        this.graph.nodes,
        (v) => d3.sum(v, (d) => d.value),
        (d) => d.layer
      );
    },
    svgNode: function () {
      return this.$refs.svgcanvas
        ? d3.select(this.$refs.svgcanvas).node()
        : null;
    },
  },
  watch: {
    chartData: function () {
      // on next tick
      this.$nextTick(() => {
        this.updateChart("watch");
      });
    },
    totalWidth: function () {
      this.updateChart();
    },

    autoSorting(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.sankey.iterations(this.autoSorting ? 6 : 0);
        this.updateChart();
      }
    },
  },
  mounted() {
    //do something after mounting vue instance
    this.initChart();
  },
  methods: {
    initChart() {
      this.sankey = this.sankeyGenerator();
      // console.log(JSON.stringify(this.chartData.nodes));
      // console.log(JSON.stringify(this.chartData.links));

      try {
        this.graph = this.sankey({
          nodes: this.chartData.nodes.map((d) => Object.assign({}, d)),
          links: this.chartData.links.map((d) => Object.assign({}, d)),
        });
      } catch (e) {
        console.log(e);
        return;
      }

      //this.sankey(this.chartData)

      let svg = d3.select(this.$refs.svg);
      svg.selectAll("g").remove();
      // add in the links
      /*var links =*/
      svg.append("g").classed("links", true);

      // add in the nodes
      svg.append("g").classed("nodes", true);

      //this.defs = svg.append('defs')

      this.updateChart("init chart mounted");
    },
    updateChart(from) {
      this.sankey = this.sankeyGenerator();
      let svg = d3.select(this.$refs.svg);
      this.graph = this.sankey({
        nodes: this.chartData.nodes.map((d) => Object.assign({}, d)),
        links: this.chartData.links.map((d) => Object.assign({}, d)),
      });
      this.$emit(" ");
      //this.computeLastColumnElements()
      this.$nextTick(function () {
        // esto es un truqui... si se llama al codigo de abajo directamente el resultado del emit de recalcule no se ha computado todavia
        let links_update = svg
          .select("g.links")
          .selectAll("path")
          .data(this.graph.links, (d) => d.id);

        let links_enter = links_update.enter().append("path");

        links_enter
          .merge(links_update)
          .attr("class", (d) => {
            return (
              "link " +
              "n-" +
              slugify(d.source.id, { strict: true, lower: true }) +
              " n-" +
              slugify(d.target.id, { strict: true, lower: true })
            );
          })
          .transition()
          .duration(1000)
          .attr("d", d3sankey.sankeyLinkHorizontal())
          .attr("fill", "none")
          .attr("stroke-width", (d) => d.width)
          .attr("stroke-opacity", 0.7)
          .style("stroke", (d) => {
            if (d.source.type === nodeTypes.category)
              return this.color(d.source.id);
            else return this.color(d.target.id);
          });

        links_enter
          .on("mousemove", (event, d) => {
            d3.select(event.target).raise();
            this.tooltip.show(
              event.pageX,
              event.pageY,
              this.tooltipId,
              "4chart"
            );
            this.hoveredData.source_title = d.source.name;
            this.hoveredData.target_title = d.target.name;
            this.hoveredData.value = d.value;

            d3.selectAll(".link").classed("greyed", true);

            d3.select(event.target).classed("greyed", false);

            //       grey out nodes
            d3.selectAll("g.node").classed("greyed", true);
            // select adjacent nodes
            d3.selectAll(
              "g.node.n-" + slugify(d.source.id, { strict: true, lower: true })
            ).classed("greyed", false);
            d3.selectAll(
              "g.node.n-" + slugify(d.target.id, { strict: true, lower: true })
            ).classed("greyed", false);
          })
          .on("mouseout", (event) => {
            this.tooltip.hide();
            // d3.select(event.target)
            //   //.attr("stroke", (d)=> { return this.color(d) } )
            //   .classed("selected", false);
            d3.selectAll(".link").classed("greyed", false);
            d3.selectAll("g.node").classed("greyed", false);
          })
          .on("click", (event, d) => {
            this.$emit("linkclick", d);
          });

        links_update.exit().remove();

        let nodes_update = svg
          .select("g.nodes")
          .selectAll("g.node")
          .data(this.graph.nodes, (d) => d.name);

        let nodes_enter = nodes_update
          .enter()
          .append("g")
          .attr(
            "class",
            (d) => "n-" + slugify(d.id, { strict: true, lower: true })
          )
          .classed("node", true)
          .classed(
            "interactive",
            (d) => d.type !== !d.name.includes("Bilateral")
          );
        nodes_enter
          .merge(nodes_update)
          .attr("transform", (d) => getNodePosition(d))
          .on("click", (event, d) => {
            this.$emit("nodeSelected", d);
            if (d.type === "category") {
              this.clickedCategory = d.name;
              this.showCategoriesModal = true;
            }
          });

        //nested general update pattern: https://bl.ocks.org/curran/1b162368fd3b5a3c623b038c4cd64d37
        nodes_enter
          .append("rect")
          .merge(nodes_update.select("rect"))
          .attr("fill", (d) =>
            d.type === nodeTypes.category
              ? "#fff"
              : d.name.includes("regional")
              ? "#FECDCE"
              : "#D4D5E1"
          )
          .attr("stroke", (d) => {
            // if( d.type === nodeTypes.category) console.log('updating nodes rect: ' + d.name +' ' + this.color(d))
            return d.type === nodeTypes.category
              ? this.color(d.name)
              : "#262E6A";
          })
          .transition()
          .duration(1000)
          .attr("x", 0)
          .attr("y", 0)
          .attr("width", (d) => getNodeWidth(d))
          .attr("height", (d) => d.y1 - d.y0)
          .attr("opacity", 0.8);

        //.append("title")

        // add in the title for the nodes
        let textnode = nodes_enter
          .append("text")
          .merge(nodes_update.select("text"))
          .attr("y", (d) => {
            return 0;
          })
          .attr("text-anchor", (d) => {
            if (d.layer === 0) return "end";
            if (d.layer === 2) return "start";
            else return "middle";
          })
          .attr("x", (d) => {
            // this part doesn't actually do anything since the position is adjusted with the inner tspan
            if (d.layer === 0) return -TEXT_PADDING;
            else if (d.layer === 2) return TEXT_PADDING + SMALL_NODE_WIDTH;
            else return CENTER_NODE_WIDTH / 2;
          })
          .attr("class", (d) => {
            if (d.type === "category") return "category";
            else return "entity";
          })
          .attr("transform", null)
          .each(function (p, j) {
            d3.select(this).selectAll("*").remove(); // for some reason
            const name =
              p.type === nodeTypes.category ? p.name.toUpperCase() : p.name;
            // if(p.type === nodeTypes.category) console.log('updating nodes text' + p.name)
            const h = p.y1 - p.y0 - NODE_PADDING * 2 - 4;
            const hTotal = p.y1 - p.y0;
            const texts = splitText(
              name,
              p.type === nodeTypes.category
                ? CENTER_NODE_WIDTH - 4
                : NODE_WIDTH,
              h,
              p.type === nodeTypes.category ? 4 : 2
            );
            if (texts.truncated) {
              // remove existing tspan nodes
              // if(p.type === nodeTypes.category) console.log('truncated nodes text' + p.name)
              d3.select(this).selectAll("*").remove();
              return;
            }
            // vertical center point of the text considering the number of lines
            const y0 = hTotal / 2 - (texts.lines.length * 14) / 2 + 7;
            d3.select(this)
              .selectAll("tspan")
              .data(texts.lines)
              .enter()
              .append("tspan")
              .attr("alignment-baseline", "central")
              .attr("y", (d, i) => {
                return y0 + i * 14;
              })
              .attr("x", (d) => {
                if (p.layer === 0) return -TEXT_PADDING;
                else if (p.layer === 2) return TEXT_PADDING + SMALL_NODE_WIDTH;
                else return CENTER_NODE_WIDTH / 2 - TEXT_PADDING / 2;
              })

              .text((d) => d);
          });

        nodes_update.exit().remove();
        textnode.exit().remove();
        // MOUSE INTERACTION
        nodes_enter
          .on("mouseover", (event, d) => {
            // console.log(d);
            //this.tooltip.tooltip_msg = `${d.name} <br/> ${d.value}`

            this.tooltip.show(
              event.pageX,
              event.pageY,
              this.tooltipId,
              "4chart"
            );
            this.hoveredData.source_title = d.name;
            this.hoveredData.target_title = "";
            this.hoveredData.value = d.value;
            this.hoveredData.type = d.type;
            d3.select(event.target).classed("selected", d.name);
            // grey out the other nodes and links and highlight only the ones connected to this node
            d3.selectAll(".link").classed("greyed", true);

            d3.selectAll(
              ".link.n-" + slugify(d.id, { strict: true, lower: true })
            ).classed("greyed", false);
          })

          .on("mouseout", (event) => {
            this.tooltip.hide();
            d3.select(event.target)
              //.attr("stroke", (d)=> { return this.color(d) } )
              .classed("selected", false);
            d3.selectAll("g.node").classed("greyed", false);
            d3.selectAll(".link").classed("greyed", false);
          });
      }); // end next tick
    },
    sankeyGenerator() {
      return (
        d3sankey
          .sankey() //generator
          //.nodeAlign(d3[`sankey${align[0].toUpperCase()}${align.slice(1)}`])
          .nodeWidth(NODE_WIDTH)
          .nodePadding(NODE_PADDING)
          .nodeAlign(d3sankey.sankeyJustify)
          .nodeId((d) => {
            return d.id;
          })
          .iterations(this.autoSorting ? 6 : 0)
          .extent([
            [0, 0],
            [
              this.width - SANKEY_GLOBAL_PADDING_X * 2,
              this.height - SANKEY_GLOBAL_PADDING_Y * 2,
            ],
          ])
      );
    },

    getLinkID(d) {
      return d.id;
    },

    color(d) {
      const color = categoryColorScale;
      return color(d);
    },
    /*computeLastColumnElements() {
      this.lastColumnElements = this.chartData.nodes.filter((d) => {
        return d.layer === this.numberLayers
      })
    }*/
    getCategoryDefinition(category) {
      return categoriesDefinition.find((c) => c.name === category)?.description;
    },
  },
};
</script>
<style lang="css">
.greyed {
  opacity: 0.2;
}

.nodes text {
  font-size: 14px;
  font-weight: bold;
  pointer-events: none;
  fill: #222;
}

.node.interactive {
  cursor: pointer;
}

.nodes text.entity tspan {
  font-weight: 300;
  font-size: 12px;
}

.nodes text.category tspan {
  font-weight: 400;
  font-size: 11px;
}
.node rect.selected {
  fill: #dad5d5 !important;
  stroke-width: 2px;
}

path.link.selected {
  stroke: black !important;
}

/* #tooltip {
  position: absolute;
  top: 400px;
  left: 100px;
  padding: 0.5em 1em;
  background: rgba(255, 255, 255, 0.98);
  border: none;
  border-radius: 6px;
  pointer-events: none;
  transition: all;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
  font-size: 14px;
}

#tooltip .tooltip-h {
  font-size: 18px;
  color: #444;
} */

#chart-container {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  position: relative;
}

#columns-valores {
  width: 200px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  position: relative;
}

#columns-valores .elemento-referencia {
  position: absolute;
  color: black;
}

#columns-valores .elemento-valor-actual {
  position: absolute;
  color: #779;
  font-weight: bold;
}

#columns-valores #column-actual {
  width: 45px;
  height: 100%;
  border-right: 1px solid #efefef;
}
</style>
